import { useContext, useEffect, useRef, useState } from 'react';
import LayersContext from '../context/LayersContext';
import memphisImg from '../assets/DJI_20250823105113_0020_D-min.jpeg';
import GanttChart from '../components/charts/GanttChart';
import ImprovementsCard from '../components/storymap/ImprovementsCard';
import ImpactCard from '../components/storymap/ImpactCard';
import HouseIcons from '../components/storymap/icons/HouseIcons';
import CarIcons from '../components/storymap/icons/CarIcons';
import CrossingIcon from '../components/storymap/icons/CrossingIcon';
import { fetchSymbolicLayers } from '../utils/dashboardUtils';
import { useOrgContext } from '../context/OrgContext';
import { SymbolicLayer } from '../services/types';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import AnchorAreas from '../components/storymap/AnchorAreas';
import { clearAllLayers, handleClearSelection, updateDashboard } from '../utils/messages';
import anchorPhoto from '../assets/Initial Anchor Area 1 (F&OC)-min.jpeg';
import anchorPhoto2 from '../assets/Initial Anchor Area 2 (Pop&Al)-min.jpeg';
import Divider from '../components/Divider';

const Storymap: React.FC = () => {
    const { layers, loading, fetchLayersData } = useContext(LayersContext);
    const { locationId } = useOrgContext();
    // Div reference objects for observer effects
    const headerRef = useRef<HTMLDivElement>(null);
    const planningRef = useRef<HTMLDivElement>(null);
    const radiusRef = useRef<HTMLDivElement>(null);
    
    const isIframe = window !== window.parent;
    const origin = '*';
    
    const [activeLayer, setActiveLayer] = useState<string | null>(null);
    const [symbolicLayers, setSymbolicLayers] = useState<SymbolicLayer[] | null>(null);
    const [scrollingUp, setScrollingUp] = useState(false);
    const [hasClearedSelection, setHasClearedSelection] = useState(false);

    // Get the layer items from the active layer
    const activeLayerData = layers?.find(layer => layer.layer_name === activeLayer);
    const layerItems = activeLayerData?.items || [];

    // Check if layers and items are loaded
    const isLoading = !layers || layers.length === 0 || layerItems?.length === 0;

    // Hide dashboard on load
    useEffect(() => {
        updateDashboard(false);
    }, []);

    // Scroll event to detect scrolling direction
    useEffect(() => {
        let lastScrollTop = 0;

        const onScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            if (scrollTop < lastScrollTop) {
                setScrollingUp(true);
            } else {
                setScrollingUp(false);
            }
            lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
        };

        window.addEventListener('scroll', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, []);

    // Reset the hasClearedSelection flag when the user scrolls down past the header
    useEffect(() => {
        if (!scrollingUp) {
            setHasClearedSelection(false); // Reset the flag when scrolling down
        }
    }, [scrollingUp]);

    // Set current active layer when layers updates
    useEffect(() => {
        if (layers.length > 0 && !activeLayer) {
            console.log('Setting active layer:', activeLayer);
            setActiveLayer(layers[0].layer_name);
        }
    }, [layers]);

    // Fetch symbolic layers when location changes
    useEffect(() => {
        (async () => {
            try {
                const data = await fetchSymbolicLayers(locationId);
                setSymbolicLayers(data);
            } catch (error) {
                console.error("Failed to fetch symbolic layers:", error);
            }
        })();
    }, [locationId]);

    // Handle message content from the map application and refresh layer data on updates
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            console.log('Storymap Received message:', event.data, 'at', window.origin, 'Received from:', event.origin);
    
            if (event.data.message === 'update') {
                const { params } = event.data;
        
                if (params) {
                    // Set orgId from the message, default to 9 if not provided
                    const orgId = params.organization_id || 9;
                    const locationId = params.location_id || null;
                    const boundingBox = params.area_view?.bounding_box || null;
                    const excludeIds = params.area_view?.exclude_ids || null;

                    // Refetch layers data as orgId changes
                    fetchLayersData(orgId, locationId, boundingBox, excludeIds);
                }
            }   
        };
    
        if (isIframe) {
            window.addEventListener("message", handleMessage);
            console.log('Message Event Listener Added by Dashboard!')
        } 
    
        return () => {
            if (isIframe) window.removeEventListener('message', handleMessage);
            console.log('Message Event Listener Removed by Dashboard!')
        };
    }, [isIframe]);


    //#region INTERSECTION OBSERVERS

    // Intersection Observer to detect when the header comes into view
    useIntersectionObserver(headerRef, (isVisible) => {
        if (isVisible && scrollingUp && !hasClearedSelection) {
            // If header is visible, scrolling up, and clear selection hasn't been sent
            handleClearSelection();
            clearAllLayers();
            setHasClearedSelection(true); // Set the flag to prevent further messages
        }
    }, { threshold: 0.5 });

    // Observer for symbolic layer toggle
    useIntersectionObserver(planningRef, (isVisible) => {
        const planningLayer = symbolicLayers?.find(layer => layer.label?.toLowerCase() === 'planning districts');

        if (isVisible && planningLayer) {
            // Hide dashboard
            updateDashboard(false);

            // If scrolling up, clear overlays and layers before showing planning layer again
            if (scrollingUp) {
                handleClearSelection();
                clearAllLayers();
            }

            // Show planning symbolic layer
            window.parent.postMessage(
                {
                    message: "toggleSymbolicLayer",
                    data: [{ label: planningLayer.label, url: planningLayer.url }],
                },
                origin
            )
            console.log('Toggle symbolic layer message sent:', [{ label: planningLayer.label, url: planningLayer.url }]);
        }
    }, { threshold: 1, rootMargin: `0px 0px -${window.innerHeight / 3}px 0px`});

    // Observer effect for showing selected radius around program point 
    useIntersectionObserver(radiusRef, (isVisible) => {
        if (isVisible && layerItems.length > 0) {
            // Distance represents number of miles wide the radius should be on the map
            const distance = 5;
            const { latitude, longitude } = layerItems[0];
            // Show radius around current selected point item (set timeout for 2 seconds)
            setTimeout(() => {
                window.parent.postMessage(
                    {
                        message: "showRadius",
                        data: {
                            latitude,
                            longitude,
                            distance 
                        },
                    },
                    origin
                );
                console.log("Show radius message sent:", { distance, latitude, longitude });
            }, 1500)
        }
    }, { threshold: 0.75, rootMargin: "0px 0px 0px 0px" });

    //#endregion

    return (
        <>
            {/* Show loading indicator if data is still loading */}
            {(isLoading || loading) && (
                <div className="loading-container-storymap">
                    <div className="loading-spinner-white"></div>
                </div>
            )}

            {/* Memphis Storymap Content */}
            <div className="p-6 w-full">
                {/* Header image */}
                <div className='flex items-center justify-center' ref={headerRef}>
                    <div className="w-full max-w-5xl">
                        <img
                        src={memphisImg}
                        alt="Memphis riverside"
                        className="w-full h-auto"
                        />
                    </div>
                </div>

                {/* Header text */}
                <div className='flex flex-col items-center justify-center mt-4'>
                    <h2 className='text-memphis-blue text-2xl font-bold'>Activating Memphis 3.0:</h2>
                    <h3 className='text-lg font-semibold mt-2'>Transforming Communities through Vision and Investments</h3>
                    <p className='text-lg max-w-sm text-center my-10'>Through Accelerate Memphis, the City is investing $75 million to breathe new life into our neighborhoods, creating spaces where everyone can thrive.</p>
                </div>

                {/* Gantt Chart for Memphis Comprehensive Plan */}
                <div className='mt-16'>
                    <GanttChart/>
                </div>

                {/* Indicate new section with divider component */}
                <Divider/>

                {/* Introduce Planning Districts -- and turn on planning district layer */}
                <div className='flex flex-col items-center justify-center' ref={planningRef}>
                    <h3 className='text-lg font-semibold my-8'>Improving Neighborhoods with Investments in 34 Anchor Areas</h3>
                    <p className=' max-w-3xl mb-4'>In 2016, the City of Memphis embarked on a two-year comprehensive planning process. The plan, known as Memphis 3.0, was the first for the City since 1981. The planning process and subsequent adoption of the Memphis 3.0 Comprehensive Plan in 2019 signaled a new era for the City. No longer planning in an ad-hoc manner, the Plan established a roadmap for future development and a new vision: Build Up, Not Out. Memphis 3.0 aims to transform Memphis into a more prosperous and inclusive city by guiding growth and policy for the next 20 years.</p>
                    <p className=' max-w-3xl my-4'>The planning process identified 14 distinct planning districts in the City. A unique and unified vision, with shared goals and strategic actions, was developed with residents and community stakeholders with the intention of guiding future investments and land use decisions. With Memphis 3.0 and the applicable planning district's vision, priorities, and actions as a foundation, small area planning further engages community members to develop implementation plans for public realm improvement projects in their community. The small area planning process provides a deeper level of site-specific analysis, design standards, and detail that a citywide plan cannot—resulting in a targeted guide for community investment projects.</p>
                </div>

                {/* Impact Section - clear search selection and turn on planning projects (put pins on map) */}
                <div className='flex flex-col items-center justify-center my-4'>
                    <p className='text-lg max-w-2xl text-center my-6'>Working with the teams of engineers, designers, and construction managers, the Division of Planning and Development is overseeing the implementation of these public improvements at 34 sites through Accelerate Memphis.</p>
                </div>
                
                {/* Improvement Cards */}
                <div className='grid grid-cols-3 place-items-center space-x-2'>
                    <ImprovementsCard num={9} text='Large Scale Investments' val='36000000' />
                    <ImprovementsCard num={23} text='Early-Stage Investments' val='9200000' />
                    <ImprovementsCard num={2} text='Intersection Safety Improvements' val='2300000' />
                </div>
                
                {/* Overall Impact */}
                <div className='flex flex-col items-center justify-center mt-20'>
                    <h2 className='text-xl font-semibold'>Overall Impact</h2>
                    <p className='text-md max-w-lg text-center mt-4'>Overall key outcomes across all areas.</p>
                    <p className='text-md max-w-lg text-center'>Click on the map to explore the outcomes in specific areas.</p>
                </div>

                {/* Impact Cards */}
                <div className='grid grid-cols-2 place-items-center mt-8'>
                    <ImpactCard imageComponent={<HouseIcons/>} header='OPPORTUNITY' description='807 buildings are no longer vacant within a half-mile of Accelerate sites'/>
                    <ImpactCard imageComponent={<CrossingIcon/>} header='CONNECTIVITY' description='8,000 feet in new bike and walkway paths added'/>
                    <div className='col-span-2 flex items-center'>
                        <ImpactCard imageComponent={<CarIcons/>} header='SAFETY' description='20% reduction in traffic crashes at improved intersections'/>
                    </div>
                </div>

                {/* Indicate new section with divider component */}
                <Divider/>

                {/* 9 Initial Anchor Areas and Construction Projects */}
                <div className='flex flex-col items-center justify-center'>
                    {/* Image Section */}
                    <div className='flex flex-col md:flex-row gap-4 mb-6 w-full max-w-[54rem]'>
                        <img 
                            src={anchorPhoto} 
                            alt='Initial Anchor Area - F&OC' 
                            className='w-full md:w-1/2 object-cover rounded shadow'
                        />
                        <img 
                            src={anchorPhoto2} 
                            alt='Initial Anchor Area - Pop&Al' 
                            className='w-full md:w-1/2 object-cover rounded shadow'
                        />
                    </div>
                    <h3 className='text-xl font-semibold mb-8'>Initial Anchor Areas</h3>
                    <p className='max-w-3xl mb-4'>Nine priority anchor areas that intersect with high frequency transit or bus rapid transit, and exhibit opportunities for greater private sector investment on the heels of public investment. Improvements in these areas will be directed by a small area plan that takes a deeper look at the types of public investments best suited to accelerate change in these areas.</p>
                    <div>
                        <AnchorAreas layerItems={layerItems} symbolicLayers={symbolicLayers} activeLayerData={activeLayerData}/>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Storymap;